/* eslint-disable jsx-a11y/anchor-is-valid */
import { faBell } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inMarketTheme } from '@inmarket/inmarkit';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import MediaTypeImage from '../../assets/media-type-display.svg';
import MediaTypeMoments from '../../assets/media-type-moments.svg';
import MediaTypeOOH from '../../assets/media-type-ooh.svg';
import MediaTypeSocial from '../../assets/media-type-social.svg';
import MediaTypeVideo from '../../assets/media-type-video.svg';
import { lineItemFormText } from '../../constants/lineItems';
import useModal from '../../hooks/useModal';
import FormSection from '../forms/FormSection';
import FormSubsection from '../forms/FormSubsection';
import RadioGroup from '../forms/RadioGroup';
import Select from '../forms/Select';
import BreakingFlex from '../shared/BreakingFlex';
import LineItemSpendModal from './LineItemSpendModal';

function LineItemMediaSection() {
  const form = useFormContext();
  const {
    getValues,
    watch,
    formState: {
      defaultValues: { mediaType: defaultMediaType },
    },
  } = form;

  const lineItemId = getValues('lineItemId');
  const contractId = getValues('contractId');

  const mediaType = watch('mediaType');

  const [showSpendModal, hideSpendModal] = useModal(
    () => <LineItemSpendModal lineItemId={lineItemId} contractId={contractId} onClose={hideSpendModal} />,
    [lineItemId, contractId],
    { clickOutsideCloses: true }
  );

  const [openMediaTypeModal, closeMediaTypeModal] = useModal(
    () => (
      <Dialog open>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <FontAwesomeIcon icon={faBell} />
            <Stack>Reminder</Stack>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Changing the media type will remove all associated creatives from the ad groups after saving this line
            item. Please remember to associate required creatives upon update.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMediaTypeModal} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    ),
    []
  );

  return (
    <FormSection title={lineItemFormText.sectionTitles.mediaAndChannels}>
      <FormSubsection>
        <BreakingFlex breakpoint="lg" sx={{ mt: 2 }}>
          <Stack spacing={2} mb={2}>
            <InputLabel>Media Type</InputLabel>
            <RadioGroup
              sx={{
                flexWrap: 'nowrap',
              }}
              name="mediaType"
              normalizeOutput={(e) => {
                const isDirty = defaultMediaType !== e.target.value;
                if (isDirty) {
                  openMediaTypeModal();
                }
                return e.target.value;
              }}
              options={[
                {
                  value: 'display',
                  label: 'Display',
                  options: { description: 'Image, HTML5, and Native Ads', image: MediaTypeImage },
                },
                {
                  value: 'video',
                  label: 'Video',
                  options: { description: 'Mobile, Desktop/Mobile Web, or CTV', image: MediaTypeVideo },
                },
                {
                  value: 'moments',
                  label: 'Moments',
                  options: { description: 'InMarket Moment Ads', image: MediaTypeMoments },
                },
                {
                  value: 'social',
                  label: 'Social',
                  options: { description: 'Social Media Ads', image: MediaTypeSocial, disabled: true },
                },
                {
                  value: 'ooh',
                  label: 'Digital OOH',
                  options: { description: 'Digital Out-of-Home Ads', image: MediaTypeOOH, disabled: true },
                },
              ]}
              renderOption={({ value, label, options }) => (
                <Card
                  key={value}
                  variant="outlined"
                  sx={{
                    width: '100%',
                    mr: 2,
                    '&:last-child': { mr: 0 },
                    borderColor: mediaType === value && inMarketTheme.palette.primary.main,
                  }}
                >
                  <Stack sx={{ position: 'relative' }}>
                    <Stack sx={{ position: 'absolute', right: 0, top: 0 }}>
                      <Radio key={value} value={value} size="small" disabled={options?.disabled} />
                    </Stack>
                    <img src={options?.image} alt={value} width="100%" />
                    <Stack p={2}>
                      <Typography variant="body2">{label}</Typography>
                      <Typography variant="caption" color="#616161">
                        {options?.description}
                      </Typography>
                    </Stack>
                  </Stack>
                </Card>
              )}
            />
          </Stack>
        </BreakingFlex>
        <BreakingFlex breakpoint="lg" sx={{ mt: 2 }}>
          <Select
            name="thinklabState"
            label="GeoLink Campaign Request Status"
            fullWidth
            options={[
              { value: 'approved', label: 'Approved' },
              { value: 'approved_no_assets', label: 'Approved - Assets Pending' },
              { value: 'rejected', label: 'Contact Support' },
              { value: 'submitted', label: 'Submitted (Under Review)' },
            ]}
          />
        </BreakingFlex>
      </FormSubsection>
      <FormSubsection title={lineItemFormText.subsectionTitles.reportLinks}>
        <BreakingFlex breakpoint="md">
          <Button
            onClick={() =>
              window.open(
                `//malibu-production.thinknearhub.com/campaign_creatives/search_insertion_order/${lineItemId}`,
                '_blank'
              )
            }
          >
            Find In Malibu
          </Button>
          <Button onClick={showSpendModal}>View Daily Spend Activity</Button>
        </BreakingFlex>
      </FormSubsection>
    </FormSection>
  );
}

export default LineItemMediaSection;
